const _ = require('lodash');
const ObjectStore = require('../objects/objectStore');
const FlagDimensions = require('../reportData/flagDimensions.js');
const EffectiveUserSettings = require('./effectiveUserSettings');
const { MAGIC_BLOCK_STR, MAGIC_BLOCK_NR } = require('../reportData/constants');

const { values: RevenueTypes } = FlagDimensions.FlagDimsByName.revenueType;

// Helper-functions to handle both user-objects that are actual user-objects, and such that are IdentyStore (frontend)
const isAdmin = (user) => (typeof user?.isAdministrator === 'function' ? user.isAdministrator() : user.isAdministrator);
const effective = (user) => new EffectiveUserSettings(user.effectiveUserSettingsArr);
const fld = (user, field) => {
	const obj = _.isFunction(user?.user) ? user.user() : user;
	return obj?.[field];
};

/** Represents the visibility settings for a type of object (currently Publisher / SSP /Revenue Type) */
class Visibility {
	constructor(settings) {
		Object.assign(this, {
			desc: settings.type,
			magicBlockValue: MAGIC_BLOCK_STR,
			...settings,
		});
	}

	// Returns true if 'user' have access to all objects
	allowAll(user) {
		return !user || isAdmin(user) || fld(user, 'permissions')?.[this.hasAllPermissionField];
	}

	// Default implementation to list all objects (override for types not in ObjectStore, e.g. Revenue Type)
	objects() {
		return Object.values(ObjectStore.byIdMap(this.model));
	}

	// Returns, according the 'user' objects - an array of ids that are allowed, or null if there are no restrictions.
	allowLimitIds(user) {
		if (this.allowAll(user)) {
			return null;
		}
		const autoAccess = this.autoAccessIds?.(user) || []; // Visibility that can't be disabled (user.publisherId)
		const specificAccess = fld(user, this.specificField) || [];
		return _.uniq(_.filter([...autoAccess, ...specificAccess]));
	}

	// Default settings when 'user' is creating a new user
	defaultNewUserSettings(user) {
		const all = this.defaultAll && this.allowAll(user);
		// If the default is to allow all but 'user' lacks that permission, copy the specific field
		const specific = this.defaultAll && !all ? fld(user, this.specificField) : [];
		return {
			[this.specificField]: specific,
			permissions: {
				[this.hasAllPermissionField]: !!all,
			},
		};
	}

	/** List allowed objects for the user. If 'allAsNull' is set, returning null means access to all objects.
	 * TODO: Doesn't work in frontend as ObjectStore is usually not loaded with all necessary objects. */
	allowedObjects(user, allAsNull) {
		const allObjects = this.objects(user);
		const allowedIds = this.allowLimitIds(user);
		if (!allowedIds) {
			return allAsNull ? null : allObjects;
		}
		const byId = _.keyBy(allObjects, 'id');
		return _.filter(allowedIds.map((id) => byId[id]));
	}

	allSetOnUserObject(userObj) {
		return fld(userObj, 'permissions')?.[this.hasAllPermissionField];
	}

	/** Returns true if 'user' is allowed to update/add user 'newOther' with selected visibility permissions.
	 * If it's for an update, then 'oldOther' is the user data before the update. The updated user is allowed
	 * to keep permissions that user lacks if they were present already before the update */
	canUpdateUserPermissions(user, newOther, oldOther) {
		const allSet = (u) => this.allSetOnUserObject(u);
		if (this.allowAll(user) || !newOther || (oldOther && allSet(oldOther))) {
			return true;
		}
		if (allSet(newOther) && !allSet(oldOther)) {
			return false;
		}
		if (!this.specificField) {
			return true;
		}
		const allowed = [
			...(oldOther?.[this.specificField] || []),
			...fld(user, this.specificField),
		];
		return !fld(newOther, this.specificField)?.some((id) => !allowed.includes(id));
	}

	/** Update 'whereIn' according to the visibility permissions of 'user', OR use the predefined visibilities
	 * in 'predefinedLimitIds', example { publisherId: ['abcd'], ... }. */
	updateWhereIn(whereIn, user, predefinedLimitIds) {
		let allowedIds;
		if (predefinedLimitIds) { // use any [dim]: [...allowedIds] or [type]: [...allowedIds] found
			allowedIds = _.find([this.type, ...this.whereIn].map((f) => predefinedLimitIds[f]));
		} else if (typeof window === 'undefined') { // backend
			const allowed = this.allowedObjects(user, true);
			if (!allowed) {
				return; // all allowed
			}
			allowedIds = _.map(allowed, 'id');
		} else { // frontend
			allowedIds = this.allowLimitIds(user);
		}
		if (!allowedIds) {
			return; // all allowed
		}
		const byId = _.keyBy(allowedIds);
		this.whereIn.forEach((dim) => {
			whereIn[dim] = whereIn[dim]?.length ? whereIn[dim].filter((id) => byId[id]) : [...allowedIds];
			if (!whereIn[dim].length) {
				// User shouldn't get any report-data at all, but whereIn: { [dim]: [] } means no filtering, so "block"
				// the report
				whereIn[dim] = [this.magicBlockValue];
			}
		});
	}
}

// Object types where we support setting visibility per user.
const settings = [
	{
		type: 'publisher',
		defaultAll: false,
		hasAllPermissionField: 'hasAllPubAccess',
		specificField: 'additionalPubAccess',
		whereIn: ['publisherId'],
		model: 'Publisher',
		autoAccessIds: (user) => _.filter([(fld(user, 'publisherId') || '').toString()]),
		frontendList: ({ Reports }) => Reports.getPublishers(),
	},
	{
		type: 'ssp',
		desc: 'SSP',
		defaultAll: true,
		hasAllPermissionField: 'hasAllSspAccess',
		specificField: 'specificSspAccess',
		whereIn: ['sspId', 'bidder'],
		model: 'BaseSsp',
		frontendList: ({ BaseSsp }) => BaseSsp.getAll(),
	},
	{
		type: 'revType',
		desc: 'revenue type',
		defaultAll: true,
		hasAllPermissionField: 'hasAllRevTypeAccess',
		specificField: 'specificRevTypeAccess',
		whereIn: ['revenueType'],
		objects: () => RevenueTypes.map(({ name, desc }) => ({ id: name, name: desc })),
		frontendList(__, user) {
			return this.allowedObjects(user);
		},
	},
	{
		type: 'userNr',
		desc: 'portfolio',
		customStorage: true,
		whereIn: ['userNr'],
		autoAccessIds: (user) => (user?.portfolioNr ? [user?.portfolioNr] : []),
		allowAll: (user) => !user || !effective(user).limitToPortfolio,
		allSetOnUserObject: (user) => !user.effectiveUserSettings.limitToPortfolio,
		magicBlockValue: MAGIC_BLOCK_NR,
		objects(user) {
			return this.autoAccessIds(user).map((id) => ({ id }));
		},
	},
];

// The 'Visibilities' singleton we're exporting from this file, as an array of Visibility-objects with some
// additional helper-functions/fields defined below.
const Visibilities = settings.map((s) => new Visibility(s));

const UserSchemaVisibilities = Visibilities.filter((v) => !v.customStorage);

const ByType = _.keyBy(Visibilities, 'type');

Object.assign(Visibilities, {
	UserSchemaVisibilities,
	SpecificFields: _.filter(_.map(Visibilities, 'specificField')),
	ByType,
	...ByType,

	// Example => { hasAllPubAccess: { type: Boolean, default: true }, ... }
	permissionSchema: Object.assign({}, ...UserSchemaVisibilities.map((v) => ({
		[v.hasAllPermissionField]: { type: Boolean, default: v.defaultAll },
	}))),

	// Example => { additionalPubAccess: [String], ... }
	userSchema: Object.assign({}, ...UserSchemaVisibilities.map((v) => ({
		[v.specificField]: [String],
	}))),

	/** Returns true if 'user' is allowed to update/add user 'newOther' with selected visibility permissions.
	 * If it's for an update, then 'oldOther' is the user data before the update. The updated user is allowed
	 * to keep permissions that user lacks if they were present already before the update */
	canUpdateUserPermissions: (user, newOther, oldOther) => Visibilities.every((v) => (
		v.canUpdateUserPermissions(user, newOther, oldOther)
	)),

	/** Update 'whereIn' according to the visibility permissions of 'user', OR use the predefined visibilities
	 * in 'predefinedLimitIds', example { publisherId: ['abcd'], ... }. */
	updateWhereIn: (whereIn, user, predefinedLimitIds) => Visibilities.forEach((v) => (
		v.updateWhereIn(whereIn, user, predefinedLimitIds)
	)),

	/** Returns a 'whereIn' object, according to visibility permission for 'user' OR using the predifined
	 * visibilities in 'predefinedLimitIds', example { publisherId: ['abcd'], ... }. */
	allowedWhereIn: (user, predefinedLimitIds) => {
		const whereIn = {};
		Visibilities.updateWhereIn(whereIn, user, predefinedLimitIds);
		return whereIn;
	},

	/** Returns true if 'whereIn' matches the visibility permissions for 'user'. This means that if there are
	 * any limits of e.g. publisher visibility, then whereIn.publisherId must be a non-empty array containing
	 * only publishers that the user have access to. */
	isAllowedWhereIn: (whereIn, user) => !_.entries(Visibilities.allowedWhereIn(user)).find(([k, allowed]) => (
		!whereIn?.[k]?.length || whereIn[k].some((e) => !allowed.includes(e))
	)),

	/** Default settings when 'user' is creating a new user */
	defaultNewUserSettings: (user) => _.merge({}, ...UserSchemaVisibilities.map((v) => v.defaultNewUserSettings(user))),
});

module.exports = Visibilities;
